/**
 * HAVEN Executive Dashboard — McKinsey-grade execution results viewer
 * Renders agent outputs as polished, printable executive briefings
 */
import { useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent } from "../components/ui/dialog";
import {
  BarChart3, Brain, CheckCircle, Clock, DollarSign,
  Layers, Zap, ChevronDown, ChevronRight, Download, Printer,
  ExternalLink, Building2, Scale, Paintbrush, Truck, HardHat,
  Landmark, Users, Leaf, Megaphone, Cpu, FileText, Eye,
} from "lucide-react";

const DIVISION_META = {
  L:  { name: "Land Intelligence",      icon: Building2,  color: "#3b82f6" },
  R:  { name: "Regulatory Navigation",  icon: Scale,      color: "#8b5cf6" },
  D:  { name: "Design Optimization",    icon: Paintbrush,  color: "#f97316" },
  S:  { name: "Supply Chain",           icon: Truck,      color: "#14b8a6" },
  C:  { name: "Construction",           icon: HardHat,    color: "#eab308" },
  F:  { name: "Financial Engineering",  icon: Landmark,   color: "#22c55e" },
  RE: { name: "Resident Services",      icon: Users,      color: "#ec4899" },
  O:  { name: "Property Operations",    icon: Leaf,       color: "#06b6d4" },
  P:  { name: "Policy & Advocacy",      icon: Megaphone,  color: "#f43f5e" },
  X:  { name: "Platform Command",       icon: Cpu,        color: "#a855f7" },
};

function getDivisionPrefix(nodeId) {
  if (!nodeId) return null;
  if (nodeId.startsWith("RE_")) return "RE";
  if (nodeId === "HAVEN_MASTER" || nodeId === "HAVEN_EXEC") return "MASTER";
  for (const prefix of ["L", "R", "D", "S", "C", "F", "O", "P", "X"]) {
    if (nodeId.startsWith(prefix + "_")) return prefix;
  }
  return null;
}

export function isHavenExecution(executionDetail) {
  if (!executionDetail) return false;
  const name = (executionDetail.swarm_name || executionDetail.template_name || "").toLowerCase();
  if (name.includes("haven")) return true;
  const results = executionDetail.agent_results || {};
  const nodeIds = Object.keys(results);
  if (nodeIds.length < 50) return false;
  return nodeIds.filter((id) => getDivisionPrefix(id) !== null).length > nodeIds.length * 0.5;
}

/* Markdown renderer with executive styling */
function ExecutiveMarkdown({ content }) {
  if (!content) return null;
  return (
    <ReactMarkdown
      components={{
        h1: ({ children }) => <h1 className="exec-h1">{children}</h1>,
        h2: ({ children }) => <h2 className="exec-h2">{children}</h2>,
        h3: ({ children }) => <h3 className="exec-h3">{children}</h3>,
        h4: ({ children }) => <h4 className="exec-h4">{children}</h4>,
        p:  ({ children }) => <p className="exec-p">{children}</p>,
        ul: ({ children }) => <ul className="exec-ul">{children}</ul>,
        ol: ({ children }) => <ol className="exec-ol">{children}</ol>,
        li: ({ children }) => <li className="exec-li">{children}</li>,
        strong: ({ children }) => <strong className="exec-strong">{children}</strong>,
        em: ({ children }) => <em className="exec-em">{children}</em>,
        blockquote: ({ children }) => <blockquote className="exec-blockquote">{children}</blockquote>,
        table: ({ children }) => <div className="exec-table-wrap"><table className="exec-table">{children}</table></div>,
        thead: ({ children }) => <thead className="exec-thead">{children}</thead>,
        tbody: ({ children }) => <tbody>{children}</tbody>,
        tr: ({ children }) => <tr className="exec-tr">{children}</tr>,
        th: ({ children }) => <th className="exec-th">{children}</th>,
        td: ({ children }) => <td className="exec-td">{children}</td>,
        hr: () => <hr className="exec-hr" />,
        code: ({ inline, children }) =>
          inline ? <code className="exec-code-inline">{children}</code> : <pre className="exec-code-block"><code>{children}</code></pre>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

/* Agent detail card — expandable with full output */
function AgentResultCard({ result, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <div className="exec-agent-card" data-testid={`agent-card-${result.node_id}`}>
      <button onClick={() => setExpanded(!expanded)} className="exec-agent-header">
        <div className="exec-agent-header-left">
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          <span className="exec-agent-label">{result.label}</span>
          <span className="exec-agent-type">{result.type}</span>
        </div>
        <div className="exec-agent-meta">
          <span>{(result.tokens || 0).toLocaleString()} tok</span>
          <span>${(result.cost || 0).toFixed(4)}</span>
          <span>{result.latency_ms}ms</span>
        </div>
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="exec-agent-body">
              <ExecutiveMarkdown content={result.output} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* Division section with agents */
function DivisionSection({ prefix, meta, results }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = meta.icon;
  const tokens = results.reduce((s, r) => s + (r.tokens || 0), 0);
  const cost = results.reduce((s, r) => s + (r.cost || 0), 0);
  // Find the synthesizer/orchestrator for this division
  const synthResult = results.find(r =>
    r.node_id?.endsWith("_synth") || r.node_id?.endsWith("_orch") ||
    r.label?.toLowerCase().includes("synthesiz") || r.label?.toLowerCase().includes("orchestrat")
  );
  const agentResults = results.filter(r => r !== synthResult);

  return (
    <div className="exec-division" style={{ "--div-color": meta.color }} data-testid={`exec-division-${prefix}`}>
      <button onClick={() => setExpanded(!expanded)} className="exec-division-header">
        <div className="exec-division-icon" style={{ background: `${meta.color}15`, color: meta.color }}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="exec-division-info">
          <h3 className="exec-division-name">{meta.name}</h3>
          <p className="exec-division-stats">{results.length} agents &middot; {tokens.toLocaleString()} tokens &middot; ${cost.toFixed(2)}</p>
        </div>
        <div className="exec-division-toggle">
          {expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </div>
      </button>

      {/* Division synthesis — always visible */}
      {synthResult && (
        <div className="exec-division-synthesis">
          <div className="exec-synth-label">
            <Zap className="w-3.5 h-3.5 text-amber-500" />
            <span>Division Synthesis</span>
          </div>
          <ExecutiveMarkdown content={synthResult.output} />
        </div>
      )}

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="exec-division-agents">
              <p className="exec-division-agents-label">{agentResults.length} Individual Agent Reports</p>
              {agentResults.map(r => (
                <AgentResultCard key={r.node_id} result={r} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* Full-screen report window */
function FullReportWindow({ executionDetail, onClose, divisionGroups, masterResults }) {
  const handlePrint = () => window.print();
  const name = executionDetail.swarm_name || "HAVEN Execution";
  const execBrief = masterResults.find(r => r.node_id === "HAVEN_EXEC");
  const masterOrch = masterResults.find(r => r.node_id === "HAVEN_MASTER");

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] p-0 bg-white dark:bg-[#0f172a] overflow-hidden" data-testid="full-report-dialog">
        {/* Header */}
        <div className="exec-report-toolbar no-print">
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{name} — Full Report</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-1.5" /> Print / PDF
            </Button>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </div>
        </div>

        <div className="exec-report-body overflow-y-auto max-h-[85vh] px-8 py-6" id="exec-report-content">
          {/* Report Title */}
          <div className="exec-report-header">
            <h1 className="exec-report-title">{name}</h1>
            <p className="exec-report-subtitle">
              Execution {executionDetail.execution_id} &middot; {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </p>
            <div className="exec-report-meta-row">
              <span>{Object.values(executionDetail.agent_results || {}).length} Agents</span>
              <span>{Object.keys(divisionGroups).length} Divisions</span>
              <span>${(executionDetail.total_cost || 0).toFixed(2)}</span>
              <span>{executionDetail.duration_ms ? `${(executionDetail.duration_ms / 1000).toFixed(0)}s` : "—"}</span>
            </div>
          </div>

          <hr className="exec-hr" />

          {/* Executive Briefing */}
          {execBrief && (
            <div className="exec-section">
              <h2 className="exec-section-title">Executive Briefing</h2>
              <ExecutiveMarkdown content={execBrief.output} />
            </div>
          )}

          {masterOrch && (
            <div className="exec-section">
              <h2 className="exec-section-title">Master Orchestrator Summary</h2>
              <ExecutiveMarkdown content={masterOrch.output} />
            </div>
          )}

          <hr className="exec-hr" />

          {/* Division Reports */}
          {Object.entries(DIVISION_META).map(([prefix, meta]) => {
            const results = divisionGroups[prefix] || [];
            if (results.length === 0) return null;
            const synthResult = results.find(r =>
              r.node_id?.endsWith("_synth") || r.node_id?.endsWith("_orch") ||
              r.label?.toLowerCase().includes("synthesiz") || r.label?.toLowerCase().includes("orchestrat")
            );
            return (
              <div key={prefix} className="exec-section page-break-before">
                <h2 className="exec-section-title" style={{ color: meta.color }}>
                  {meta.name}
                  <span className="exec-section-count">{results.length} agents</span>
                </h2>
                {synthResult && <ExecutiveMarkdown content={synthResult.output} />}
                <div className="exec-agent-grid">
                  {results.filter(r => r !== synthResult).map(r => (
                    <div key={r.node_id} className="exec-agent-mini">
                      <h4 className="exec-agent-mini-title">{r.label}</h4>
                      <ExecutiveMarkdown content={r.output} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}


export default function HavenExecutiveDashboard({ executionDetail }) {
  const [showFullReport, setShowFullReport] = useState(false);

  const { divisionGroups, masterResults, stats } = useMemo(() => {
    const results = Object.values(executionDetail.agent_results || {});
    const groups = {};
    const masters = [];
    results.forEach((r) => {
      const prefix = getDivisionPrefix(r.node_id);
      if (prefix === "MASTER" || !prefix) { masters.push(r); }
      else { if (!groups[prefix]) groups[prefix] = []; groups[prefix].push(r); }
    });

    const totalAgents = results.length;
    const totalCost = results.reduce((s, r) => s + (r.cost || 0), 0);
    const totalTokens = results.reduce((s, r) => s + (r.tokens || 0), 0);
    return {
      divisionGroups: groups,
      masterResults: masters,
      stats: { totalAgents, totalCost, totalTokens, divisions: Object.keys(groups).length },
    };
  }, [executionDetail]);

  const name = executionDetail.swarm_name || executionDetail.template_name || "HAVEN Execution";
  const execBrief = masterResults.find(r => r.node_id === "HAVEN_EXEC");
  const masterOrch = masterResults.find(r => r.node_id === "HAVEN_MASTER");

  return (
    <div className="exec-container" data-testid="haven-executive-dashboard">
      {/* Executive Styles */}
      <style>{`
        .exec-container { font-family: 'Inter', system-ui, sans-serif; }

        /* Executive Briefing Section */
        .exec-briefing {
          background: linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,41,59,0.95));
          border: 1px solid rgba(148,163,184,0.12);
          border-radius: 16px;
          padding: 32px;
          margin-bottom: 24px;
        }
        .exec-briefing-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          color: #94a3b8;
          font-weight: 600;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 1px solid rgba(148,163,184,0.1);
        }

        /* Markdown executive styling */
        .exec-h1 { font-size: 22px; font-weight: 700; color: #f1f5f9; margin: 24px 0 12px; line-height: 1.3; }
        .exec-h2 { font-size: 17px; font-weight: 700; color: #e2e8f0; margin: 20px 0 10px; line-height: 1.35; }
        .exec-h3 { font-size: 14px; font-weight: 600; color: #cbd5e1; margin: 16px 0 8px; line-height: 1.4; }
        .exec-h4 { font-size: 13px; font-weight: 600; color: #94a3b8; margin: 12px 0 6px; }
        .exec-p  { font-size: 13px; line-height: 1.75; color: #cbd5e1; margin: 0 0 12px; }
        .exec-ul { list-style: none; padding: 0; margin: 0 0 12px; }
        .exec-ol { list-style: none; padding: 0; margin: 0 0 12px; counter-reset: item; }
        .exec-ol .exec-li { counter-increment: item; }
        .exec-ol .exec-li::before { content: counter(item) "."; color: #3b82f6; font-weight: 600; margin-right: 8px; }
        .exec-li {
          font-size: 13px; line-height: 1.7; color: #cbd5e1;
          padding: 4px 0 4px 20px; position: relative;
        }
        .exec-ul .exec-li::before {
          content: '';
          position: absolute; left: 4px; top: 12px;
          width: 6px; height: 6px; border-radius: 50%;
          background: #3b82f6;
        }
        .exec-strong { color: #f1f5f9; font-weight: 600; }
        .exec-em { color: #94a3b8; font-style: italic; }
        .exec-blockquote {
          border-left: 3px solid #3b82f6;
          padding: 12px 16px; margin: 12px 0;
          background: rgba(59,130,246,0.06); border-radius: 0 8px 8px 0;
        }
        .exec-blockquote .exec-p { color: #94a3b8; margin: 0; }
        .exec-hr { border: none; border-top: 1px solid rgba(148,163,184,0.1); margin: 24px 0; }
        .exec-table-wrap { overflow-x: auto; margin: 12px 0; border-radius: 8px; border: 1px solid rgba(148,163,184,0.1); }
        .exec-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .exec-th { padding: 10px 14px; text-align: left; background: rgba(30,41,59,0.8); color: #94a3b8; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
        .exec-td { padding: 10px 14px; color: #cbd5e1; border-top: 1px solid rgba(148,163,184,0.06); }
        .exec-tr:hover .exec-td { background: rgba(59,130,246,0.03); }
        .exec-code-inline { background: rgba(59,130,246,0.1); padding: 2px 6px; border-radius: 4px; font-size: 12px; color: #93c5fd; }
        .exec-code-block { background: rgba(15,23,42,0.6); padding: 12px 16px; border-radius: 8px; font-size: 12px; color: #94a3b8; overflow-x: auto; margin: 12px 0; }

        /* Division sections */
        .exec-division { border: 1px solid rgba(148,163,184,0.08); border-radius: 12px; overflow: hidden; margin-bottom: 8px; background: rgba(15,23,42,0.3); }
        .exec-division-header { display: flex; align-items: center; gap: 12px; padding: 16px; width: 100%; text-align: left; cursor: pointer; transition: background 0.15s; }
        .exec-division-header:hover { background: rgba(148,163,184,0.04); }
        .exec-division-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .exec-division-info { flex: 1; min-width: 0; }
        .exec-division-name { font-size: 14px; font-weight: 600; color: #e2e8f0; }
        .exec-division-stats { font-size: 11px; color: #64748b; margin-top: 2px; }
        .exec-division-toggle { color: #64748b; flex-shrink: 0; }
        .exec-division-synthesis { padding: 0 20px 16px; }
        .exec-synth-label { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid rgba(148,163,184,0.06); }
        .exec-division-agents { padding: 0 20px 16px; border-top: 1px solid rgba(148,163,184,0.06); }
        .exec-division-agents-label { font-size: 11px; color: #64748b; font-weight: 600; margin: 12px 0 8px; text-transform: uppercase; letter-spacing: 0.5px; }

        /* Agent cards */
        .exec-agent-card { border: 1px solid rgba(148,163,184,0.06); border-radius: 8px; margin-bottom: 4px; overflow: hidden; }
        .exec-agent-header { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; width: 100%; text-align: left; cursor: pointer; transition: background 0.15s; }
        .exec-agent-header:hover { background: rgba(148,163,184,0.04); }
        .exec-agent-header-left { display: flex; align-items: center; gap: 8px; min-width: 0; flex: 1; }
        .exec-agent-label { font-size: 12px; font-weight: 500; color: #e2e8f0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .exec-agent-type { font-size: 10px; color: #64748b; background: rgba(100,116,139,0.1); padding: 2px 6px; border-radius: 4px; white-space: nowrap; }
        .exec-agent-meta { display: flex; gap: 12px; font-size: 10px; color: #64748b; flex-shrink: 0; }
        .exec-agent-body { padding: 0 14px 14px 38px; }

        /* Report overlay */
        .exec-report-toolbar { display: flex; align-items: center; justify-content: space-between; padding: 12px 24px; border-bottom: 1px solid rgba(148,163,184,0.1); background: rgba(15,23,42,0.95); }
        .exec-report-header { text-align: center; margin-bottom: 32px; }
        .exec-report-title { font-size: 28px; font-weight: 700; color: #f1f5f9; margin: 0 0 8px; }
        .exec-report-subtitle { font-size: 13px; color: #64748b; }
        .exec-report-meta-row { display: flex; justify-content: center; gap: 24px; margin-top: 16px; font-size: 12px; color: #94a3b8; }
        .exec-report-meta-row span { padding: 4px 12px; border: 1px solid rgba(148,163,184,0.1); border-radius: 6px; }
        .exec-section { margin-bottom: 32px; }
        .exec-section-title { font-size: 18px; font-weight: 700; color: #e2e8f0; margin: 0 0 16px; display: flex; align-items: center; gap: 8px; }
        .exec-section-count { font-size: 12px; font-weight: 400; color: #64748b; }
        .exec-agent-grid { display: grid; grid-template-columns: 1fr; gap: 12px; margin-top: 16px; }
        .exec-agent-mini { padding: 16px; border: 1px solid rgba(148,163,184,0.06); border-radius: 8px; background: rgba(15,23,42,0.3); }
        .exec-agent-mini-title { font-size: 13px; font-weight: 600; color: #e2e8f0; margin: 0 0 8px; }

        /* Print styles */
        @media print {
          .no-print { display: none !important; }
          .exec-container { background: white !important; color: #1e293b !important; }
          .exec-h1, .exec-h2, .exec-h3, .exec-h4, .exec-strong { color: #0f172a !important; }
          .exec-p, .exec-li, .exec-td { color: #334155 !important; }
          .exec-briefing { background: #f8fafc !important; border-color: #e2e8f0 !important; }
          .exec-division { border-color: #e2e8f0 !important; break-inside: avoid; }
          .exec-agent-card { break-inside: avoid; }
          .page-break-before { page-break-before: auto; }
          .exec-report-title, .exec-section-title { color: #0f172a !important; }
          .exec-report-body { max-height: none !important; }
        }
      `}</style>

      {/* Header bar */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-emerald-400 flex items-center gap-2">
            <Zap className="w-5 h-5" /> HAVEN Executive Dashboard
          </h2>
          <p className="text-xs text-[var(--qn-foreground-muted)] mt-0.5">{name}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`text-xs ${executionDetail.status === "completed" ? "bg-emerald-600" : "bg-amber-600"}`}>
            {executionDetail.status}
          </Badge>
          <Button variant="outline" size="sm" onClick={() => setShowFullReport(true)} data-testid="open-full-report-btn">
            <ExternalLink className="w-4 h-4 mr-1.5" /> Full Report
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-report-btn">
            <Printer className="w-4 h-4 mr-1.5" /> Print / PDF
          </Button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-5" data-testid="haven-stats-bar">
        {[
          { label: "Total Agents", value: stats.totalAgents.toLocaleString(), icon: Brain, color: "#3b82f6" },
          { label: "Completion", value: "100%", icon: CheckCircle, color: "#22c55e" },
          { label: "Divisions", value: `${stats.divisions}/10`, icon: Layers, color: "#a855f7" },
          { label: "Total Cost", value: `$${stats.totalCost.toFixed(2)}`, icon: DollarSign, color: "#eab308" },
          { label: "Tokens", value: stats.totalTokens.toLocaleString(), icon: BarChart3, color: "#14b8a6" },
          { label: "Duration", value: executionDetail.duration_ms ? `${(executionDetail.duration_ms / 1000).toFixed(0)}s` : "—", icon: Clock, color: "#f97316" },
        ].map((s) => (
          <div key={s.label} className="p-3 rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)]">
            <div className="flex items-center gap-2 mb-1">
              <s.icon className="w-3.5 h-3.5" style={{ color: s.color }} />
              <span className="text-[10px] text-[var(--qn-foreground-muted)] uppercase tracking-wider">{s.label}</span>
            </div>
            <div className="text-lg font-bold text-[var(--qn-foreground)]">{s.value}</div>
          </div>
        ))}
      </div>

      {/* EXECUTIVE BRIEFING — Top priority */}
      {execBrief && (
        <div className="exec-briefing" data-testid="executive-briefing">
          <div className="exec-briefing-label">
            <FileText className="w-4 h-4" /> Executive Briefing
          </div>
          <ExecutiveMarkdown content={execBrief.output} />
        </div>
      )}

      {masterOrch && (
        <div className="exec-briefing" style={{ borderColor: "rgba(234,179,8,0.15)" }} data-testid="master-orchestrator-output">
          <div className="exec-briefing-label">
            <Brain className="w-4 h-4" /> Master Orchestrator Summary
          </div>
          <ExecutiveMarkdown content={masterOrch.output} />
        </div>
      )}

      {/* Division Reports */}
      <div className="mb-4">
        <h3 className="text-xs font-semibold text-[var(--qn-foreground-muted)] uppercase tracking-wider mb-3 flex items-center gap-2">
          <Layers className="w-3.5 h-3.5" /> Division Reports ({stats.divisions})
        </h3>
        <div className="space-y-2">
          {Object.entries(DIVISION_META).map(([prefix, meta]) => {
            const results = divisionGroups[prefix] || [];
            if (results.length === 0) return null;
            return <DivisionSection key={prefix} prefix={prefix} meta={meta} results={results} />;
          })}
        </div>
      </div>

      {/* Full Report Dialog */}
      {showFullReport && (
        <FullReportWindow
          executionDetail={executionDetail}
          onClose={() => setShowFullReport(false)}
          divisionGroups={divisionGroups}
          masterResults={masterResults}
        />
      )}
    </div>
  );
}
